import { FC, useState, useCallback, useMemo, useReducer, useEffect, useRef, CSSProperties } from "react";
import TestDropdown from "./TestDropdown";
import TestForm from "./TestForm";
import CustomSelect from "../../../../../../../CommonComponents/CustomSelect";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";
import { CriticalIcon, OnboardIcon } from "../../../../../../../Missions/Satellites/TestCase/svgs/TestCaseSvgs";
import { deriveIsCritical } from "../../../../../../../Missions/Satellites/TestCase/TestCaseHelpers";
import type { Option, TestCaseConfig, TestPlanConfig } from "../../types/consoleTypes";
import useUrlParams from "../../../../../../hooks/useUrlParams";
import { showToastMessage } from "../../../../../../../../utils/commonFunctions";
import {
  SESSION_KEY_FAILURE_HANDLING,
  SESSION_KEY_EXECUTE_ONBOARD,
  CUSTOM_EVENT_STORAGE_UPDATE,
} from "../../../../../../../../utils/constants";
import ValidateService from "../../../../../../../../service/ValidateService";

// ─── Shared utilities ──────────────────────────────────────────────────────────

function flattenObject(value: any, prefix = "", out: Record<string, string> = {}): Record<string, string> {
  if (value == null) return out;
  if (Array.isArray(value)) {
    value.forEach((v, i) => flattenObject(v, prefix ? `${prefix}[${i}]` : `[${i}]`, out));
    return out;
  }
  if (typeof value === "object") {
    Object.entries(value).forEach(([k, v]) => flattenObject(v, prefix ? `${prefix}.${k}` : k, out));
    return out;
  }
  out[prefix] = String(value);
  return out;
}

function toTestCaseConfig(src: any): TestCaseConfig | null {
  if (!src?.testCaseId || !Array.isArray(src?.testCaseDetails)) return null;
  return {
    testCaseId: src.testCaseId,
    testPlanId: src.testPlanId,
    version: src.version,
    createdAt: src.createdAt,
    updatedAt: src.updatedAt,
    testCaseDetails: src.testCaseDetails.map((entry: any, idx: number) => {
      const flattenedDetails = flattenObject(entry?.testCaseEntryDetails ?? {});
      const commandName =
        String(
          entry?.commandName ??
          entry?.CommandName ??
          entry?.testCaseEntryDetails?.commandName ??
          entry?.testCaseEntryDetails?.CommandName ??
          entry?.CommandDetails?.commandName ??
          entry?.CommandDetails?.CommandName ??
          flattenedDetails["commandName"] ??
          flattenedDetails["CommandName"] ??
          flattenedDetails["name"] ??
          flattenedDetails["Name"] ??
          entry?.name ??
          ""
        ).trim();

      const tcId =
        String(
          entry?.tcId ??
          entry?.commandId ??
          entry?.CommandId ??
          entry?.id ??
          (entry?.operationId && !String(entry.operationId).startsWith("entry-") ? entry.operationId : "") ??
          ""
        ).trim();

      return {
        operationId: String(entry?.operationId ?? "").trim() || `entry-${idx + 1}`,
        testCaseEntryType: (entry?.testCaseEntryType ?? "TC") as "TC" | "TASK",
        testCaseEntryStatus: entry?.testCaseEntryStatus ?? "",
        testCaseEntryDetails: flattenedDetails,
        isCritical: !!(entry?.isCritical ?? entry?.CommandDetails?.isCritical),
        commandName: commandName || undefined,
        tcId: tcId || undefined,
      };
    }),
    isCritical: src.isCritical !== undefined ? Boolean(src.isCritical) : deriveIsCritical(src),
    executeOnboard: Boolean(src.executeOnboard),
    executionLocation: src.executionLocation,
  };
}

function toTestCaseShellOption(testCase: any, plan?: TestPlanConfig, fallbackLabel?: string): Option<TestCaseConfig> | null {
  const testCaseId = testCase?.testCaseId;
  if (!testCaseId) return null;
  const isCritical = deriveIsCritical(testCase);
  const isOnboard = Boolean(testCase?.executeOnboard) || testCase?.executionLocation === 'ONBOARD';
  const hasAnyRightIcon = isCritical || isOnboard;
  return {
    label: testCase?.testCaseName ?? testCase?.name ?? fallbackLabel ?? testCase?.testCaseId ?? "Unnamed test case",
    value: {
      testCaseId,
      testPlanId: testCase?.testPlanId ?? plan?.testPlanId,
      version: testCase?.version,
      createdAt: testCase?.createdAt ?? plan?.createdAt,
      updatedAt: testCase?.updatedAt ?? plan?.updatedAt,
      isCritical,
      executeOnboard: isOnboard,
      executionLocation: testCase?.executionLocation || (isOnboard ? 'ONBOARD' : 'GROUND'),
    } as TestCaseConfig,
    rightIcon: hasAnyRightIcon ? (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {isCritical && (
          <CustomToolTip title="Critical Test Case" placement="top">
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <CriticalIcon />
            </span>
          </CustomToolTip>
        )}
        {isOnboard && (
          <CustomToolTip title="Onboard Execution" placement="top">
            <span className="tcd-onboard-icon-cell" style={{ display: 'inline-flex', alignItems: 'center', margin: 0 }}>
              <OnboardIcon />
            </span>
          </CustomToolTip>
        )}
      </div>
    ) : undefined,
  };
}

function toTestCaseOptionFromPlan(testCase: any, plan: TestPlanConfig): Option<TestCaseConfig> | null {
  const details = plan.testCaseDetails ?? [];
  const matchedDetail = details.find((d: any) => (d.id || d.testCaseId) === testCase.testCaseId);
  const isOnboard = Boolean(
    matchedDetail?.executionLocation === 'ONBOARD' ||
    Boolean(matchedDetail?.executeOnboard) ||
    testCase.executionLocation === 'ONBOARD' ||
    Boolean(testCase.executeOnboard)
  );
  const executionLocation = matchedDetail?.executionLocation || testCase.executionLocation || (isOnboard ? 'ONBOARD' : 'GROUND');

  const shell = toTestCaseShellOption(testCase, plan);
  const config = toTestCaseConfig({
    ...testCase,
    testPlanId: plan.testPlanId,
    createdAt: plan.createdAt,
    updatedAt: plan.updatedAt,
    executeOnboard: isOnboard,
    executionLocation,
  });
  if (!shell || !config) return null;
  return {
    ...shell,
    value: config,
  };
}

function getResponseData(res: any) {
  return res?.data?.data ?? res?.data;
}

function extractTestPlanDetail(response: any): TestPlanConfig | null {
  const payload = response?.data?.data ?? response?.data;
  if (!payload || typeof payload !== "object") return null;
  return payload as TestPlanConfig;
}

function clearStoredTestSelections(): void {
  [
    "testTestCase",
    "testCaseId",
    "testFormValues",
    "testPlanPlan",
    "testPlanCaseId",
    "testPlanFormValues",
    "testPlanId",
    "testPlanExecutionLocation",
    SESSION_KEY_EXECUTE_ONBOARD,
    SESSION_KEY_FAILURE_HANDLING,
  ].forEach((key) => sessionStorage.removeItem(key));
  window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
}

// ─── Test plan form reducer ────────────────────────────────────────────────────

type TestPlanState = {
  valuesByCase: Record<string, Record<string, string>>;
  validityByCase: Record<string, boolean>;
};

type TestPlanAction =
  | { type: "UPDATE_CASE"; caseKey: string; values: Record<string, string>; isValid: boolean }
  | { type: "RESET" };

const initialTestPlanState: TestPlanState = { valuesByCase: {}, validityByCase: {} };

function testPlanReducer(state: TestPlanState, action: TestPlanAction): TestPlanState {
  switch (action.type) {
    case "UPDATE_CASE":
      return {
        valuesByCase: { ...state.valuesByCase, [action.caseKey]: action.values },
        validityByCase: { ...state.validityByCase, [action.caseKey]: action.isValid },
      };
    case "RESET":
      return initialTestPlanState;
  }
}

const STORAGE_KEYS = {
  "TEST":      { selection: "testTestCase",  caseId: "testCaseId",      formValues: "testFormValues" },
  "TEST PLAN": { selection: "testPlanPlan",  caseId: "testPlanCaseId",  formValues: "testPlanFormValues" },
} as const;

export const TestCaseDetailsShimmer = () => (
  <div className="test__form__shimmer" aria-label="Loading test case details">
    {Array.from({ length: 4 }).map((_, i) => (
      <div className="test__entry__card test__entry__card--shimmer" key={i}>
        <div className="test__entry__card__header">
          <span className="test__shimmer test__shimmer--caret" />
          <span className="test__shimmer test__shimmer--title" style={{ '--test-shimmer-w': `${46 + (i % 3) * 12}%` } as CSSProperties} />
        </div>
        {i === 0 && (
          <div className="test__entry__card__body">
            <div className="test__entry__field__row">
              <div className="test__entry__field__col">
                <span className="test__shimmer test__shimmer--label" />
                <span className="test__shimmer test__shimmer--input" />
              </div>
              <div className="test__entry__field__col">
                <span className="test__shimmer test__shimmer--label" />
                <span className="test__shimmer test__shimmer--input" />
              </div>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────────

interface TestFormSectionProps {
  mode: "TEST" | "TEST PLAN";
  formContainerHeight: number;
  onFormChange: (values: Record<string, string>, isValid: boolean) => void;
}

const TestPlanFormShimmer: FC = () => (
  <div
    className="test__plan__loading"
    role="status"
    aria-live="polite"
    aria-label="Loading test plan details"
  >
    {[0, 1].map((item) => (
      <div className="test__plan__loading__context" key={item}>
        <div className="test__plan__shimmer test__plan__loading__label" />
        <div className="test__plan__loading__card">
          <div className="test__plan__loading__header">
            <div className="test__plan__shimmer test__plan__loading__caret" />
            <div className="test__plan__shimmer test__plan__loading__title" />
          </div>
          <div className="test__plan__loading__fields">
            {[0, 1, 2, 3].map((field) => (
              <div className="test__plan__loading__field" key={field}>
                <div className="test__plan__shimmer test__plan__loading__field-label" />
                <div className="test__plan__shimmer test__plan__loading__input" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
    <span className="test__plan__loading__sr-only">Loading test plan details</span>
  </div>
);

const TestFormSection: FC<TestFormSectionProps> = ({ mode, formContainerHeight, onFormChange }) => {
  const { mission_id, sat_id } = useUrlParams();
  const keys = STORAGE_KEYS[mode];
  const isTestPlan = mode === "TEST PLAN";

  const [selectedTestCase, setSelectedTestCase] = useState<Option<TestCaseConfig> | null>(null);
  // ─── Console Failure Handling State ───
  // We use sessionStorage to hold the failure handling mode ('ABORT_ON_FAILURE' or 'SKIP_AND_CONTINUE')
  // so that sibling execution components (e.g., FormSubmit.tsx) can easily read the state at run time.
  const [failureHandlingOption, setFailureHandlingOption] = useState<Option<string>>(() => {
    const saved = sessionStorage.getItem(SESSION_KEY_FAILURE_HANDLING);
    if (saved === 'SKIP_AND_CONTINUE') {
      return { label: "Skip & Continue", value: "SKIP_AND_CONTINUE" };
    }
    return { label: "Abort on Failure", value: "ABORT_ON_FAILURE" };
  });

  const handleFailureHandlingChange = (opt: Option<string> | null) => {
    const val = opt?.value ?? "ABORT_ON_FAILURE";
    const label = opt?.label ?? "Abort on Failure";
    setFailureHandlingOption({ label, value: val });
    sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, val);
    // Dispatch a custom event so that FormSubmit.tsx updates its execution payload in sync
    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  };
  const [executeOnboard, setExecuteOnboard] = useState<boolean>(() => {
    return sessionStorage.getItem(SESSION_KEY_EXECUTE_ONBOARD) === "true";
  });
  const previousFailureHandlingRef = useRef<Option<string> | null>(null);

  const handleExecuteOnboardChange = (checked: boolean) => {
    setExecuteOnboard(checked);
    sessionStorage.setItem(SESSION_KEY_EXECUTE_ONBOARD, checked ? "true" : "false");

    if (checked) {
      // Remember current state before checking
      previousFailureHandlingRef.current = failureHandlingOption;
      // Reset Failure Handling to default 'Abort on Failure' when checked
      const defaultOption = { label: "Abort on Failure", value: "ABORT_ON_FAILURE" };
      setFailureHandlingOption(defaultOption);
      sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, "ABORT_ON_FAILURE");
    } else {
      // When unchecked, restore previous state or default
      const restored = previousFailureHandlingRef.current ?? { label: "Abort on Failure", value: "ABORT_ON_FAILURE" };
      setFailureHandlingOption(restored);
      sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, restored.value);
    }

    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  };

  const [selectedTestPlan, setSelectedTestPlan] = useState<Option<TestPlanConfig> | null>(null);
  const [pendingTestPlan, setPendingTestPlan] = useState<Option<TestPlanConfig> | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [loadingTestCaseDetails, setLoadingTestCaseDetails] = useState(false);
  const [testPlanState, dispatchTestPlan] = useReducer(testPlanReducer, initialTestPlanState);
  const testCaseDetailsRequestRef = useRef(0);
  const [loadingTestPlan, setLoadingTestPlan] = useState(false);
  const planRequestIdRef = useRef(0);

  const normalizedTestCase = useMemo(() => {
    if (!selectedTestCase?.value) return null;
    return toTestCaseConfig(selectedTestCase.value);
  }, [selectedTestCase]);

  const normalizedPlanTestCases = useMemo(() => {
    if (!isTestPlan || !selectedTestPlan?.value?.testCases?.length) return [];
    const planId = selectedTestPlan.value.testPlanId;
    const testCaseDetails = selectedTestPlan.value.testCaseDetails ?? [];
    return selectedTestPlan.value.testCases
      .map((testCase, index) => {
        const option = toTestCaseOptionFromPlan(testCase, selectedTestPlan.value);
        const detail = testCaseDetails[index] ?? testCaseDetails.find((d: any) => d.id === testCase.testCaseId);
        const isOnboard = Boolean(
          detail?.executionLocation === 'ONBOARD' ||
          Boolean(detail?.executeOnboard) ||
          testCase.executionLocation === 'ONBOARD' ||
          Boolean(testCase.executeOnboard)
        );
        return option
          ? { option, caseKey: `${planId}:${option.value.testCaseId}:${index}`, isOnboard }
          : null;
      })
      .filter((item): item is { option: Option<TestCaseConfig>; caseKey: string; isOnboard: boolean } => Boolean(item));
  }, [isTestPlan, selectedTestPlan]);

  const isPlanAllGround = useMemo(() => {
    if (!isTestPlan || !selectedTestPlan?.value) return true;
    const plan = selectedTestPlan.value;
    if (plan.executionLocation === 'ONBOARD' || plan.executionLocation === 'MIXED') return false;
    if (Boolean((plan as any).executeOnboard)) return false;
    if (plan.testCaseDetails?.some((d: any) => d.executionLocation === 'ONBOARD' || Boolean(d.executeOnboard))) return false;
    if (plan.testCases?.some((tc: any) => tc.executionLocation === 'ONBOARD' || Boolean(tc.executeOnboard))) return false;
    return true;
  }, [isTestPlan, selectedTestPlan]);

  const isExecuteOnboardDisabled = isTestPlan && Boolean(selectedTestPlan?.value) && !isPlanAllGround;

  const isCurrentSelectionCritical = useMemo(() => {
    if (isTestPlan) {
      return Boolean(selectedTestPlan?.value?.isCritical);
    }
    return Boolean(normalizedTestCase?.isCritical || selectedTestCase?.value?.isCritical);
  }, [isTestPlan, selectedTestPlan, normalizedTestCase, selectedTestCase]);

  useEffect(() => {
    return () => {
      clearStoredTestSelections();
    };
  }, []);

  // Reset all cross-mode/context state. TEST and TEST PLAN reuse this component
  // instance, so an explicit reset prevents a plan's first case from leaking
  // into standalone TEST mode (and vice versa).
  useEffect(() => {
    testCaseDetailsRequestRef.current += 1;
    planRequestIdRef.current += 1;
    setSelectedTestCase(null);
    setSelectedTestPlan(null);
    setPendingTestPlan(null);
    setLoadingTestCaseDetails(false);
    setLoadingTestPlan(false);
    setFormValues({});
    dispatchTestPlan({ type: "RESET" });
    clearStoredTestSelections();
    setExecuteOnboard(false);
    onFormChange({}, false);
    const savedFailure = sessionStorage.getItem(SESSION_KEY_FAILURE_HANDLING);
    if (savedFailure === "ABORT_ON_FAILURE") {
      setFailureHandlingOption({ label: "Abort on Failure", value: "ABORT_ON_FAILURE" });
    } else {
      setFailureHandlingOption({ label: "Skip & Continue", value: "SKIP_AND_CONTINUE" });
      sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, "SKIP_AND_CONTINUE");
    }

    return () => {
      testCaseDetailsRequestRef.current += 1;
      planRequestIdRef.current += 1;
    };
  }, [mode, mission_id, sat_id, onFormChange]);

  // ── Sync selection state → sessionStorage ────────────────────────────────
  useEffect(() => {
    const selection = isTestPlan ? selectedTestPlan : selectedTestCase;
    if (selection) {
      // Full plan details can include large nested validation payloads. Keep
      // only lightweight selection metadata in browser storage.
      const persistedSelection = isTestPlan && selectedTestPlan
        ? {
            label: selectedTestPlan.label,
            value: {
              testPlanId: selectedTestPlan.value.testPlanId,
              testPlanName: selectedTestPlan.value.testPlanName,
            },
          }
        : selection;
      try {
        sessionStorage.setItem(keys.selection, JSON.stringify(persistedSelection));
      } catch {
        sessionStorage.removeItem(keys.selection);
      }
    } else {
      sessionStorage.removeItem(keys.selection);
    }
    if (selectedTestCase) {
      sessionStorage.setItem(keys.caseId, selectedTestCase.value.testCaseId);
    } else {
      sessionStorage.removeItem(keys.caseId);
    }
    if (isTestPlan) {
      if (selectedTestPlan) {
        sessionStorage.setItem("testPlanId", selectedTestPlan.value.testPlanId);
      } else {
        sessionStorage.removeItem("testPlanId");
      }
    }
    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  }, [isTestPlan, keys, selectedTestCase, selectedTestPlan]);

  // ── Sync form values → sessionStorage ────────────────────────────────────
  useEffect(() => {
    try {
      if (Object.keys(formValues).length > 0) {
        sessionStorage.setItem(keys.formValues, JSON.stringify(formValues));
      } else {
        sessionStorage.removeItem(keys.formValues);
      }
    } catch {
      // Storage quota must never break selection or execution state.
      sessionStorage.removeItem(keys.formValues);
    }
    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  }, [formValues, keys.formValues]);

  const fetchTestCaseDetails = useCallback(
    async (testCaseId: string, shell?: Partial<TestCaseConfig>): Promise<TestCaseConfig> => {
      if (!mission_id || !sat_id) {
        throw new Error("Missing mission or satellite id");
      }
      const res = await ValidateService.getTestCaseById(mission_id, testCaseId, sat_id);
      const detail = getResponseData(res);
      return {
        ...shell,
        ...detail,
        testCaseId,
      } as TestCaseConfig;
    },
    [mission_id, sat_id],
  );

  // ── Handlers — pure state updates only ───────────────────────────────────
  const handleTestCaseChange = useCallback(async (opt: Option<TestCaseConfig> | null) => {
    testCaseDetailsRequestRef.current += 1;
    const requestId = testCaseDetailsRequestRef.current;
    const shellOption = opt ? toTestCaseShellOption(opt.value, undefined, opt.label) : null;
    setSelectedTestCase(shellOption);
    setFormValues({});
    onFormChange({}, false);

    if (!shellOption) {
      setLoadingTestCaseDetails(false);
      setExecuteOnboard(false);
      sessionStorage.setItem(SESSION_KEY_EXECUTE_ONBOARD, "false");
      sessionStorage.removeItem("testPlanExecutionLocation");
      window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
      return;
    }

    const testCaseId = shellOption.value.testCaseId;

    setLoadingTestCaseDetails(true);
    try {
      const detail = await fetchTestCaseDetails(testCaseId, shellOption.value);
      if (testCaseDetailsRequestRef.current !== requestId) return;
      setSelectedTestCase({
        ...shellOption,
        value: detail,
      });
      const isTcOnboard = Boolean(detail?.executionLocation === 'ONBOARD' || (detail as any)?.executeOnboard);
      setExecuteOnboard(isTcOnboard);
      sessionStorage.setItem(SESSION_KEY_EXECUTE_ONBOARD, isTcOnboard ? "true" : "false");
      sessionStorage.removeItem("testPlanExecutionLocation");
      window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
    } catch {
      if (testCaseDetailsRequestRef.current === requestId) {
        setSelectedTestCase(null);
        showToastMessage("error", "Failed to load test case details");
      }
    } finally {
      if (testCaseDetailsRequestRef.current === requestId) {
        setLoadingTestCaseDetails(false);
      }
    }
  }, [fetchTestCaseDetails, onFormChange]);

  const handleTestPlanChange = useCallback(async (opt: Option<TestPlanConfig> | null) => {
    testCaseDetailsRequestRef.current += 1;
    const requestId = ++planRequestIdRef.current;
    setSelectedTestPlan(null);
    setSelectedTestCase(null);
    setPendingTestPlan(opt);
    setFormValues({});
    dispatchTestPlan({ type: "RESET" });
    clearStoredTestSelections();
    onFormChange({}, false);
    const savedFailure = sessionStorage.getItem(SESSION_KEY_FAILURE_HANDLING);
    if (savedFailure === "ABORT_ON_FAILURE") {
      setFailureHandlingOption({ label: "Abort on Failure", value: "ABORT_ON_FAILURE" });
    } else {
      setFailureHandlingOption({ label: "Skip & Continue", value: "SKIP_AND_CONTINUE" });
    }

    if (!opt) {
      setPendingTestPlan(null);
      setLoadingTestPlan(false);
      setExecuteOnboard(false);
      sessionStorage.setItem(SESSION_KEY_EXECUTE_ONBOARD, "false");
      sessionStorage.removeItem("testPlanExecutionLocation");
      window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
      return;
    }
    if (!mission_id) {
      setPendingTestPlan(null);
      setLoadingTestPlan(false);
      showToastMessage("error", "Missing mission context");
      return;
    }

    const requestedPlanId = String(opt.value.testPlanId ?? "").trim();
    if (!requestedPlanId) {
      setPendingTestPlan(null);
      setLoadingTestPlan(false);
      showToastMessage("error", "Selected test plan has no valid ID");
      return;
    }

    setLoadingTestPlan(true);
    try {
      const res = await ValidateService.getTestPlanById(mission_id, requestedPlanId);
      if (requestId !== planRequestIdRef.current) return;

      const plan = extractTestPlanDetail(res);
      const planTestCases = Array.isArray(plan?.testCases) ? plan.testCases : [];
      if (!plan?.testPlanId) throw new Error("Test plan details are unavailable");
      if (String(plan.testPlanId).trim() !== requestedPlanId) {
        throw new Error("Received details for a different test plan");
      }
      if (plan.satelliteId && sat_id && String(plan.satelliteId) !== String(sat_id)) {
        throw new Error("Selected test plan does not belong to this satellite");
      }

      const normalizedCases = planTestCases.map((testCase) => toTestCaseOptionFromPlan(testCase, plan));
      if (normalizedCases.some((testCase) => testCase === null)) {
        throw new Error("Test plan contains incomplete test case details");
      }

      const hydratedOption: Option<TestPlanConfig> = { label: opt.label, value: { ...plan, testCases: planTestCases }, rightIcon: opt.rightIcon };
      setPendingTestPlan(null);
      setSelectedTestPlan(hydratedOption);

      const details = plan.testCaseDetails ?? [];
      const isAllOnboard = plan.executionLocation === 'ONBOARD' || (
        (details.length > 0 && details.every((d: any) => d.executionLocation === 'ONBOARD' || Boolean(d.executeOnboard))) ||
        (planTestCases.length > 0 && planTestCases.every((tc: any) => tc.executionLocation === 'ONBOARD' || Boolean(tc.executeOnboard)))
      );
      const hasAnyOnboard = plan.executionLocation === 'ONBOARD' || plan.executionLocation === 'MIXED' ||
        Boolean((plan as any).executeOnboard) ||
        details.some((d: any) => d.executionLocation === 'ONBOARD' || Boolean(d.executeOnboard)) ||
        planTestCases.some((tc: any) => tc.executionLocation === 'ONBOARD' || Boolean(tc.executeOnboard));

      if (hasAnyOnboard) {
        const shouldCheck = Boolean(isAllOnboard);
        setExecuteOnboard(shouldCheck);
        sessionStorage.setItem(SESSION_KEY_EXECUTE_ONBOARD, shouldCheck ? "true" : "false");
      } else {
        setExecuteOnboard(false);
        sessionStorage.setItem(SESSION_KEY_EXECUTE_ONBOARD, "false");
      }
      if (plan.executionLocation) {
        sessionStorage.setItem("testPlanExecutionLocation", plan.executionLocation);
      } else {
        sessionStorage.removeItem("testPlanExecutionLocation");
      }
      window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));

      if (planTestCases.length === 0) {
        setSelectedTestCase(null);
        showToastMessage("info", "No test cases are available under this test plan");
        return;
      }

      setSelectedTestCase(normalizedCases[0]);
    } catch (error: any) {
      if (requestId !== planRequestIdRef.current) return;
      setPendingTestPlan(null);
      setSelectedTestPlan(null);
      setSelectedTestCase(null);
      showToastMessage(
        "error",
        error?.response?.data?.message ?? error?.message ?? "Failed to load test plan details",
      );
    } finally {
      if (requestId === planRequestIdRef.current) setLoadingTestPlan(false);
    }
  }, [mission_id, sat_id, onFormChange]);

  const handleFormChange = useCallback(
    (values: Record<string, string>, isValid: boolean) => {
      setFormValues(values);
      onFormChange(values, isValid);
    },
    [onFormChange],
  );

  const handlePlanTestCaseFormChange = useCallback((caseKey: string, values: Record<string, string>, isValid: boolean) => {
    dispatchTestPlan({ type: "UPDATE_CASE", caseKey, values, isValid });
  }, []);

  // ── Derive merged form values from reducer state ──────────────────────────
  useEffect(() => {
    if (!isTestPlan) return;
    const mergedValues = Object.entries(testPlanState.valuesByCase).reduce<Record<string, string>>(
      (acc, [caseKey, values]) => {
        Object.entries(values).forEach(([key, value]) => {
          // Entry fallback keys commonly repeat across cases; namespace them so
          // stored overrides remain lossless for multi-case plans.
          acc[`${caseKey}.${key}`] = value;
        });
        return acc;
      },
      {},
    );
    const allValid =
      normalizedPlanTestCases.length > 0 &&
      normalizedPlanTestCases.every(({ caseKey }) => testPlanState.validityByCase[caseKey] === true);
    setFormValues(mergedValues);
    onFormChange(mergedValues, allValid);
  }, [isTestPlan, testPlanState, normalizedPlanTestCases, onFormChange]);

  return (
    <div
      className="left__panel__body__form__section__container modal__scrollbar__white"
      style={{ '--ts-max-h': `calc(100vh - ${255 + formContainerHeight}px)` } as CSSProperties}
    >
      <TestDropdown
        mode={mode}
        satelliteId={sat_id ?? ""}
        value={isTestPlan ? (selectedTestPlan ?? pendingTestPlan) : selectedTestCase}
        isResolvingSelection={isTestPlan && loadingTestPlan}
        onChange={isTestPlan
          ? (opt) => { void handleTestPlanChange(opt as Option<TestPlanConfig> | null); }
          : (opt) => handleTestCaseChange(opt as Option<TestCaseConfig> | null)
        }
      />

      <div
        className={`__checkbox__container ${!isCurrentSelectionCritical ? 'left__panel__body_input__border' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '6px',
          marginBottom: !isCurrentSelectionCritical ? '8px' : '4px',
          paddingBottom: !isCurrentSelectionCritical ? '12px' : '0px',
          width: '100%',
        }}
        title={isExecuteOnboardDisabled ? "Execution location is configured per test case in this test plan" : undefined}
      >
        <input
          className="__checkbox"
          type="checkbox"
          id="executeOnboard"
          checked={executeOnboard}
          disabled={isExecuteOnboardDisabled}
          onChange={(e) => handleExecuteOnboardChange(e.target.checked)}
          style={{
            margin: 0,
            transformOrigin: 'left center',
            cursor: isExecuteOnboardDisabled ? 'not-allowed' : 'pointer',
          }}
        />
        <label
          htmlFor="executeOnboard"
          className="__checkbox__label"
          style={{
            margin: 0,
            cursor: isExecuteOnboardDisabled ? 'not-allowed' : 'pointer',
            textTransform: 'none',
            color: isExecuteOnboardDisabled ? 'rgba(240, 240, 240, 0.4)' : '#f0f0f0',
            fontSize: '12px',
            fontFamily: 'Montserrat',
            fontWeight: 400,
            lineHeight: 'normal',
            userSelect: 'none',
            transition: 'color 0.2s ease',
          }}
        >
          Execute Onboard
        </label>
      </div>

      {isTestPlan && loadingTestPlan && <TestPlanFormShimmer />}

      {isCurrentSelectionCritical && (
        <div
          className="left__panel__body__input__dropdown__container left__panel__body__input__custom__dropdown left__panel__body_input__border"
          style={{
            cursor: executeOnboard ? 'not-allowed' : 'default',
          }}
        >
          <span
            className="input__label"
            style={{
              color: executeOnboard ? 'rgba(240, 240, 240, 0.4)' : '#f0f0f0',
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 400,
              userSelect: 'none',
              transition: 'color 0.2s ease',
            }}
          >
            Failure Handling
          </span>
          <CustomSelect
            isDisabled={executeOnboard}
            options={
              isTestPlan && selectedTestPlan?.value?.testCases?.length === 1
                ? [{ label: "Abort on Failure", value: "ABORT_ON_FAILURE" }]
                : [
                    { label: "Abort on Failure", value: "ABORT_ON_FAILURE" },
                    { label: "Skip & Continue", value: "SKIP_AND_CONTINUE" },
                  ]
            }
            value={failureHandlingOption}
            placeholder="Select option"
            onChange={(opt) => handleFailureHandlingChange(opt as Option<string> | null)}
            background="rgba(10,10,10,0.65)"
            isSearchable={false}
          />
        </div>
      )}

      {isTestPlan && !loadingTestPlan && selectedTestPlan?.value?.testCases?.length === 0 && (
        <div className="left__panel__body_input__border test__plan__tc__empty">
          No test cases in this plan
        </div>
      )}

      {loadingTestCaseDetails && !isTestPlan && <TestCaseDetailsShimmer />}

      {!loadingTestCaseDetails && normalizedTestCase && !isTestPlan && (
        <div className="test__plan__context">
          <span
            className="test__plan__tc__case__label"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{selectedTestCase?.label}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {normalizedTestCase.isCritical && (
                <CustomToolTip title="Critical Test Case" placement="top" className="tp-icon-tooltip">
                  <span className="test__plan__tc__case__critical-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <CriticalIcon />
                  </span>
                </CustomToolTip>
              )}
              {(normalizedTestCase.executionLocation === 'ONBOARD' || Boolean(normalizedTestCase.executeOnboard) || executeOnboard) && (
                <CustomToolTip title="Onboard Execution" placement="top" className="tp-icon-tooltip">
                  <span className="tcd-onboard-icon-cell" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <OnboardIcon />
                  </span>
                </CustomToolTip>
              )}
            </span>
          </span>
          <TestForm testCase={normalizedTestCase} onFormChange={handleFormChange} />
        </div>
      )}

      {isTestPlan && normalizedPlanTestCases.length > 0 &&
        normalizedPlanTestCases.map(({ option: testCaseOpt, caseKey, isOnboard }) => (
          <div key={caseKey} className="test__plan__context">
            <span
              className="test__plan__tc__case__label"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <span>{testCaseOpt.label}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {testCaseOpt.value?.isCritical && (
                  <CustomToolTip title="Critical Test Case" placement="top" className="tp-icon-tooltip">
                    <span className="test__plan__tc__case__critical-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <CriticalIcon />
                    </span>
                  </CustomToolTip>
                )}
                {isOnboard && (
                  <CustomToolTip title="Onboard Execution" placement="top" className="tp-icon-tooltip">
                    <span className="tcd-onboard-icon-cell" style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <OnboardIcon />
                    </span>
                  </CustomToolTip>
                )}
              </span>
            </span>
            <TestForm
              testCase={testCaseOpt.value}
              onFormChange={(values, isValid) =>
                handlePlanTestCaseFormChange(caseKey, values, isValid)
              }
            />
          </div>
        ))
      }
    </div>
  );
};

export default TestFormSection;
